<template>
  <div>
    <navigation></navigation>
    <div class="projects__list--container">
      <h4>
        Projects
        <i class="tooltip-icon fas fa-question-circle" v-tooltip="'Custom distributions of the Rumie-Learn cloud app for your organization.'"></i>
      </h4>
      <div class="projects__list--filter">
        <rum-input icon="fa fa-search" v-model="projectSearch" class="projects__search" placeholder="Search..." clearable />
        <rum-select
          placeholder="Select a project"
          v-model="organization"
          :options="organizations"
          :clearable="false"
          class="projects__filter"
          label="name" />
        <rum-button class="project__add-button" @click="addProject"><img src="@/assets/images/plus.svg">Add Project</rum-button>
      </div>
      <rum-loader full v-if="working" style="height: 100px"/>
      <div v-else-if="filteredProjects.length <= 0" class="project__empty">
        There are no Projects available, if you are searching try another query.
      </div>
      <div v-else class="project__container">
        <table class="project__table">
          <thead>
            <tr class="project-table__row">
              <th>Name</th>
              <th>Code</th>
              <th>Last Modified</th>
              <th colspan="3"></th>
            </tr>
          </thead>
          <tbody>
            <tr class="project-table__data" v-for="(project, index) in filteredProjects" :key="index">
              <router-link class="project-edit__link" :to="{name: 'ProjectConnect', params: {id: project.id}}">
                <td style="min-width: 125px; color: var(--black)">{{project.name}}</td>
                <td>{{project.encodedID}}</td>
                <td>{{project.updatedAt | timestamp}}</td>
              </router-link>
              <td>
                <a v-if="project.google_analytics_url" class="project__action" target="_blank" :href="project.google_analytics_url">
                  <i class="fad fa-analytics"></i>
                  <span class="project-action__label">Analytics</span>
                </a>
              </td>
              <td>
                <a class="project__action" target="_blank" :href="projectUrl+project.encodedID">
                  <i class="far fa-link project-action__item"></i>
                  <span class="project-action__label">View</span>
                </a>
              </td>
              <td>
                <div class="project__action project__action--link" @click="copyText(projectUrl+project.encodedID)">
                  <i class="far fa-copy project-action__item"></i>
                  <span class="project-action__label">Copy</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/api";
import Navigation from "@/components/Navigation";

import {
  generateName
} from "@/utils/randomname";

export default {
  name: "ProjectList",
  components: { Navigation },
  data() {
    return {
      loadingProjects: true,
      projects: [],
      projectSearch: "",
      organizations: [],
      organization: null,
      working: false,
    };
  },
  computed: {
    organizationId() {
      return this.organization && this.organization.id;
    },
    projectUrl() {
      return this.$store.getters['projectEdit/projectUrl'];
    },
    filteredProjects() {
      return this.projects.filter(project => (project.name.toLowerCase()).includes(this.projectSearch.toLowerCase()))
    }
  },
  methods: {
    copyText(text) {
      this.$copyToClipboard(text);
      this.$notify({
        type: "success",
        title: "Text copied successfully"
      });
    },
    async addProject() {
      try {
        this.working = true;
        const current = api('v3');
        const { data } = await current.post(`connect/organizations/${this.organizationId}/projects`, {
          name: generateName(),
          internet_connectivity: true,
          timezone: "America/Detroit",
          language: "eng",
        });
        this.$router.push({
          name: "ProjectConnect",
          params: { id: data.id }
        });
      } catch (error) {
        console.log(error);
        const errorMessage = error.response.data.message;
        this.$notify({
          type: "error",
          title: errorMessage || "Failed to create Project, please try again."
        });
      } finally {
        this.working = false;
      }
    },
    async fetchProjects() {
      const current = api('v3');
      this.working = true;
      const { data } = await current.get(`connect/organizations/${this.organizationId}/projects`);
      this.projects = data.projects || [];
      this.working = false;
    },
    async fetchOrganizations() {
      const current = api('v3');
      this.working = true;
      const response = await current.get(`users/organizations`);
      this.organizations = response.data;
      if(this.organizations.length > 1) {
        this.organization = this.organizations.filter(organization => organization.id === 5)
        this.organization = this.organization[0]
      } else {
        this.organization = this.organizations[0]
      }
      this.working = false;
    },
  },
  watch: {
    organizationId: {
      immediate: true,
      handler() {
        if(this.organizationId) {
          this.fetchProjects();
        }
      }
    }
  },
  mounted() {
    this.fetchOrganizations();
  }
};
</script>

<style scoped>
.projects__list--container {
  background: var(--white);
  padding: var(--spacing-4);
  margin: var(--spacing-4);
  margin-bottom: 75px;
  box-shadow: var(--shadow-primary);
}

.projects__list--filter {
  display: flex;
}

.projects__search {
  width: 100%;
  margin-right: var(--spacing-4);
}

.projects__filter {
  width: 100%;
}

.project__add-button {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: var(--spacing-3) var(--spacing-6) !important;
  font-size: var(--sm) !important;
  margin-left: 15px;
}

.project__add-button img {
  margin-right: var(--spacing-2);
}

.project__table {
  border-collapse: collapse;
  width: 100%;
}

.project__empty {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: var(--base);
  color: var(--grey);
  background: var(--white);
  height: 100%;
  text-align: center;
  padding: var(--spacing-8);
  margin: 0 var(--spacing-4);
}

.project__container {
  overflow-x: auto;
  background-color: var(--white);
  margin-top: var(--spacing-4);
}

th {
  padding: var(--spacing-3) var(--spacing-4);
  font-size: var(--xs);
  text-align: left;
  color: var(--grey-dark);
}

td {
  padding: var(--spacing-3) var(--spacing-4);
  font-size: var(--sm);
  text-align: left;
  color: var(--grey-dark);
}

.project-table__row {
  background: var(--grey-lighter);
}

.project-table__data {
  border-bottom: var(--border-1) solid var(--grey-lighter);
}

.project-table__data:last-child {
  border-bottom: none;
}

.project-edit__link {
  text-decoration: none;
  display: contents;
}

.project__action {
  display: flex;
  align-items: center;
  color: var(--primary);
  cursor: pointer;
  background: none;
  border: none;
  text-decoration: none;
  max-width: 75px;
}

.project__action--link {
  max-width: 100%;
}

.project__action:disabled {
  opacity: var(--opacity-50);
  cursor: not-allowed;
}

.project-action__item {
  font-size: var(--base);
}

.project-action__label {
  font-weight: 600;
  margin-left: var(--spacing-2);
}

@media screen and (max-width: 700px) {
  .projects__list--filter {
    flex-direction: column;
  }

  .projects__filter {
    margin-top: var(--spacing-4);
  }

  .project__add-button {
    margin-top: var(--spacing-4);
    margin-left: 0;
    text-align: center;
  }
}
</style>
